import Button from 'antd/lib/button';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import message from 'antd/lib/message';
import Switch from 'antd/lib/switch';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { axiosPostCall } from '../api/axiosCall';
import CommonLayout from '../common/CommonLayout';
import DataTable from '../common/DataTable';
import { getColumnSearchProps } from '../common/DataTableSearchTemplate';
import PriceCategoryModal from '../common/PriceCategoryModal';
import SalesPersonModal from '../common/SalesPersonModal';
import AddUserModal from '../common/AddUserModal';
import { API_END_POINT } from '../constant/constants';
import { AuthContext } from '../context/AuthProvider';

const Users = React.memo(() => {
  const [userList, setUserList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [editCompanyId, setEditCompanyId] = useState();
  const [isPriceListModalOpen, setIsPriceListModalOpen] = useState(false);
  const [isSalesPersonModalOpen, setIsSalesPersonModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [currentSalesPerson, setCurrentSalesPerson] = useState('');
  const [pageConfig, setPageConfig] = useState({
    page: 1,
    pageSize: 10,
    total: 10,
    pageCount: 1,
  });
  const [filterConfig, setFilterConfig] = useState({
    pageSize: 10,
    pageNumber: 1,
  });

  const { token } = useContext(AuthContext);
  const debounceTimeoutRef = useRef(null);

  // Debounced filter update function
  const debouncedUpdateFilter = useCallback((newConfig) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setFilterConfig(newConfig);
    }, 300); // 300ms debounce delay
  }, []);

  const getUserList = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.GET_USER_LIST,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          ...filterConfig,
        },
      });
      if (res.hideErrorFromUser) {
        setUserList(res.data.companyResponses);
        setPageConfig(res.data.pagination);
      } else {
        if (!res.data.companyResponses.length) {
          message.error(res.error.message);
        }
        setUserList(res.data.companyResponses);
        setPageConfig(res.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching user list:', error);
      message.error('Failed to fetch user list');
    } finally {
      setIsLoading(false);
    }
  }, [token, filterConfig]);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleUserApprovalChange = useCallback(
    async (record) => {
      if (record.productPriceCategoryAccess.length && record.salesPerson) {
        setIsLoading(true);
        try {
          const res = await axiosPostCall({
            apiEndPoint: API_END_POINT.UPDATE_USER_APPROVAL,
            header: {
              Authorization: `Bearer ${token}`,
            },
            body: {
              companyId: record.companyId,
              userId: record.userId,
              salesPersonName: record.salesPerson || '',
              isApprove: !record.isApproved,
            },
          });
          if (res?.hideErrorFromUser) {
            message.success(res.data.message);
            getUserList();
          } else {
            message.error(res?.error?.message);
          }
        } catch (error) {
          console.error('Error updating user approval:', error);
          message.error('Failed to update user approval');
        } finally {
          setIsLoading(false);
        }
      } else {
        message.info(
          'Please Assign Price and sales person Before Mark Company as Approved.',
        );
      }
    },
    [token, getUserList],
  );

  const handlePriceListChangeClick = useCallback((companyId) => {
    setEditCompanyId(companyId);
    setIsPriceListModalOpen(true);
  }, []);

  const handleSalesPersonChangeClick = useCallback((companyId, salesPerson) => {
    setEditCompanyId(companyId);
    setCurrentSalesPerson(salesPerson || '');
    setIsSalesPersonModalOpen(true);
  }, []);

  const handleAddUserClick = useCallback(() => {
    setIsAddUserModalOpen(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        key: 'fullName',
        title: 'Full Name',
        dataIndex: 'fullName',
        ...getColumnSearchProps('fullName'),
      },
      {
        key: 'companyName',
        title: 'Company Name',
        dataIndex: 'companyName',
        ...getColumnSearchProps('companyName'),
      },
      {
        key: 'salesPerson',
        title: 'Sales Person',
        dataIndex: 'salesPerson',
        width: 200,
        render: (e, record) => (
          <div className='flex flex-col'>
            <button
              className='text-blue-900 underline font-bold'
              onClick={() => {
                handleSalesPersonChangeClick(
                  record.companyId,
                  record.salesPerson,
                );
              }}
            >
              Change
            </button>
            <span className='text-sm'>{record.salesPerson || '-'}</span>
          </div>
        ),
      },
      {
        key: 'mobileNo',
        title: 'Mobile No',
        dataIndex: 'mobileNo',
        ...getColumnSearchProps('mobileNo'),
      },
      {
        key: 'gstNumber',
        title: 'GST Number',
        dataIndex: 'gstNumber',
        ...getColumnSearchProps('gstNumber'),
      },
      {
        key: 'priceList',
        title: 'Price List',
        dataIndex: 'priceList',
        width: 200,
        render: (e, record) => (
          <div className='flex flex-col'>
            <button
              className='text-blue-900 underline font-bold'
              onClick={() => {
                handlePriceListChangeClick(record.companyId);
              }}
            >
              Change
            </button>
            {record.productPriceCategoryAccess.length > 0 ? (
              record.productPriceCategoryAccess.map((priceDetails) => (
                <span
                  key={priceDetails.priceCategoryId}
                  className={`${priceDetails.isSelected ? 'text-red-900 font-bold text-sm' : 'text-xs'}`}
                >
                  {priceDetails.priceCategoryDescription}
                </span>
              ))
            ) : (
              <>-</>
            )}
          </div>
        ),
      },
      {
        title: 'Approved',
        render: (e, record) => (
          <Switch
            checked={record.isApproved}
            onChange={() => {
              handleUserApprovalChange(record);
            }}
          />
        ),
      },
    ],
    [
      handlePriceListChangeClick,
      handleSalesPersonChangeClick,
      handleUserApprovalChange,
    ],
  );

  const handleChange = useCallback(
    ({ pagination, filters }) => {
      let newConfig = {
        ...filterConfig,
        pageNumber: pagination.current,
      };

      if (pagination.pageSize !== filterConfig.pageSize) {
        newConfig = {
          ...newConfig,
          pageNumber: 1,
          pageSize: pagination.pageSize,
        };
      }

      // Optimize filter handling
      const filterMappings = {
        companyName: 'companyName',
        fullName: 'fullName',
        salesPerson: 'salesPerson',
        gstNumber: 'gstNo',
        mobileNo: 'mobileNo',
      };

      Object.entries(filterMappings).forEach(([filterKey, configKey]) => {
        if (filters[filterKey]) {
          newConfig[configKey] = filters[filterKey][0];
        } else {
          delete newConfig[configKey];
        }
      });

      // Check if filters changed (for debouncing)
      const hasFilterChanges = Object.keys(filterMappings).some((filterKey) => {
        const currentValue = filterConfig[filterMappings[filterKey]];
        const newValue = filters[filterKey] ? filters[filterKey][0] : undefined;
        return currentValue !== newValue;
      });

      // Use debounced update for search filters, immediate update for pagination
      if (hasFilterChanges) {
        newConfig.pageNumber = 1; // Reset to first page when filtering
        debouncedUpdateFilter(newConfig);
      } else {
        setFilterConfig(newConfig);
      }
    },
    [filterConfig, debouncedUpdateFilter],
  );

  const handlePriceListModelCloseClick = useCallback(() => {
    setIsPriceListModalOpen(false);
    setEditCompanyId();
  }, []);

  const handleSalesPersonModalCloseClick = useCallback(() => {
    setIsSalesPersonModalOpen(false);
    setEditCompanyId();
    setCurrentSalesPerson('');
  }, []);

  const handlePriceListCallback = useCallback(() => {
    handlePriceListModelCloseClick();
    getUserList();
  }, [handlePriceListModelCloseClick, getUserList]);

  const handleSalesPersonCallback = useCallback(() => {
    handleSalesPersonModalCloseClick();
    getUserList();
  }, [handleSalesPersonModalCloseClick, getUserList]);

  const handleAddUserModalCloseClick = useCallback(() => {
    setIsAddUserModalOpen(false);
  }, []);

  const handleAddUserCallback = useCallback(() => {
    handleAddUserModalCloseClick();
    getUserList();
  }, [handleAddUserModalCloseClick, getUserList]);

  // Memoize title with Add User button
  const tableTitle = useMemo(
    () => (
      <div className='flex justify-between items-center'>
        <h2 className='text-xl font-semibold text-text'>Users Management</h2>
        <Button
          type='primary'
          onClick={handleAddUserClick}
          icon={<PlusOutlined />}
          className='border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
        >
          Add User
        </Button>
      </div>
    ),
    [handleAddUserClick],
  );

  // Memoize DataTable props to prevent unnecessary re-renders
  const dataTableProps = useMemo(
    () => ({
      isTableRowEditable: false,
      data: userList,
      columns,
      isSpinning: isLoading,
      title: tableTitle,
      rowKey: 'userId',
      handleChange,
      total: pageConfig.total,
      currentPage: filterConfig.pageNumber,
    }),
    [
      userList,
      columns,
      isLoading,
      tableTitle,
      handleChange,
      pageConfig.total,
      filterConfig.pageNumber,
    ],
  );

  return (
    <CommonLayout>
      <PriceCategoryModal
        isOpen={isPriceListModalOpen}
        companyId={editCompanyId}
        callback={handlePriceListCallback}
        handlePriceListModelCloseClick={handlePriceListModelCloseClick}
      />
      <SalesPersonModal
        isOpen={isSalesPersonModalOpen}
        companyId={editCompanyId}
        currentSalesPerson={currentSalesPerson}
        callback={handleSalesPersonCallback}
        handleSalesPersonModalCloseClick={handleSalesPersonModalCloseClick}
      />
      <AddUserModal
        isOpen={isAddUserModalOpen}
        callback={handleAddUserCallback}
        handleAddUserModalCloseClick={handleAddUserModalCloseClick}
      />
      <DataTable {...dataTableProps} />
    </CommonLayout>
  );
});

export default Users;
